import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {downloadApk, stopBeforeDownload} from '@/utils';
import {getModalMaskClassName} from '@/components/basic/modal/mask';

type PromotionDownloadModalProps = {
  open: boolean;
  imageUrl?: string;
  onClose: () => void;
};

type DownloadModalCloseButtonProps = {
  className: string;
  onClose: () => void;
  title: string;
};

function DownloadModalCloseButton({
  className,
  onClose,
  title,
}: DownloadModalCloseButtonProps) {
  return (
    <button
      type="button"
      aria-label={title}
      onClick={onClose}
      className={`flex items-center justify-center border-0 bg-transparent p-0 text-[#9FA5AC] active:opacity-80 ${className}`}>
      <span className="relative h-3 w-3">
        <span className="absolute left-1/2 top-1/2 h-0.5 w-3 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-current" />
        <span className="absolute left-1/2 top-1/2 h-0.5 w-3 -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-current" />
      </span>
    </button>
  );
}

export function PromotionDownloadModal({
  open,
  imageUrl,
  onClose,
}: PromotionDownloadModalProps) {
  const {t} = useTranslation();
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openFrameRef = useRef<number | null>(null);
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (openFrameRef.current) {
      cancelAnimationFrame(openFrameRef.current);
      openFrameRef.current = null;
    }

    if (open) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setMounted(true);
      openFrameRef.current = requestAnimationFrame(() => {
        openFrameRef.current = requestAnimationFrame(() => {
          setVisible(true);
          openFrameRef.current = null;
        });
      });
      return;
    }

    setVisible(false);
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setMounted(false);
      closeTimerRef.current = null;
    }, 320);
  }, [open]);

  useEffect(
    () => () => {
      if (openFrameRef.current) {
        cancelAnimationFrame(openFrameRef.current);
      }
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    },
    [],
  );

  const handleDownload = () => {
    if (stopBeforeDownload()) {
      return;
    }
    downloadApk();
  };

  if (!mounted || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className={getModalMaskClassName(visible, 'z-[10000]')}
      role="dialog"
      aria-modal="true"
      aria-label={t('label.download')}>
      {imageUrl ? (
        <section
          className={`absolute left-1/2 top-1/2 flex max-h-[90vh] w-[20rem] -translate-x-1/2 flex-col items-center transition-all duration-300 ease-out ${
            visible
              ? '-translate-y-1/2 scale-100 opacity-100'
              : '-translate-y-[45%] scale-95 opacity-0'
          }`}>
          <DownloadModalCloseButton
            className="absolute right-4 top-4 h-6 w-6"
            onClose={onClose}
            title={t('common.close')}
          />
          <button
            type="button"
            aria-label={t('label.download')}
            onClick={handleDownload}
            className="max-h-[80vh] w-full overflow-hidden rounded-[0.75rem] border border-[color:var(--image)] bg-white p-0 active:scale-[0.99]">
            <img
              src={imageUrl}
              alt={t('label.download')}
              className="block max-h-[80vh] w-full object-contain"
            />
          </button>
        </section>
      ) : (
        <section
          className={`absolute left-1/2 top-1/2 flex w-[20rem] -translate-x-1/2 flex-col gap-4 rounded-2xl border border-[color:var(--image)] bg-[var(--BG)] px-5 py-6 text-[var(--T1)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] transition-all duration-300 ease-out ${
            visible
              ? '-translate-y-1/2 scale-100 opacity-100'
              : '-translate-y-[45%] scale-95 opacity-0'
          }`}>
          <DownloadModalCloseButton
            className="absolute right-4 top-4 h-6 w-6"
            onClose={onClose}
            title={t('common.close')}
          />
          <p className="pr-6 text-base font-semibold">{t('label.download')}</p>
          <button
            type="button"
            onClick={handleDownload}
            className="flex h-12 w-full items-center justify-center rounded-xl border-0 bg-[var(--1)] font-semibold text-white active:scale-[0.99]">
            {t('label.download')}
          </button>
        </section>
      )}
    </div>,
    document.body,
  );
}
